import type { EditableRow, Workstream } from "../types";
import { EditableField } from "./EditableField";
import { StatusBadge } from "./StatusBadge";

interface Props {
  ws: Workstream;
  active: boolean;
  onClick: () => void;
  onUpdate: (next: Workstream) => void;
}

function countRows(rows: EditableRow[]): number {
  let n = 0;
  for (const r of rows) {
    n += 1;
    if (r.children) n += r.children.length;
  }
  return n;
}

export function WorkstreamCard({ ws, active, onClick, onUpdate }: Props) {
  function set<K extends keyof Workstream>(k: K, v: Workstream[K]) {
    onUpdate({ ...ws, [k]: v } as Workstream);
  }

  const rowCount = countRows(ws.rows ?? []);
  const noun = rowCount === 1 ? "item" : "items";

  return (
    <div
      className={`rounded-2xl p-5 transition-all ${
        active
          ? "bg-white ring-2 ring-blue-400 shadow-lg"
          : "bg-white ring-1 ring-slate-200 hover:ring-slate-300 hover:shadow-md"
      }`}
    >
      <div
        className="flex items-start justify-between gap-2 mb-1 cursor-pointer"
        onClick={onClick}
      >
        <div className="flex-1 min-w-0">
          <EditableField
            value={ws.name}
            onChange={(v) => set("name", v)}
            multi
            className="font-semibold text-sm text-slate-800 leading-snug w-full"
          />
        </div>
        <div className="shrink-0">
          <StatusBadge status={ws.status} onChange={(v) => set("status", v)} />
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <span className="text-[11px] text-slate-400 tabular-nums">
          {rowCount} {noun}
        </span>
      </div>
      <div className="mt-3 space-y-1.5 cursor-pointer" onClick={onClick}>
        <div className="flex gap-2 text-xs items-center">
          <span className="text-slate-400 w-14 shrink-0">DF Lead</span>
          <EditableField
            value={ws.deerfieldLead}
            onChange={(v) => set("deerfieldLead", v)}
            className="text-slate-700 font-medium text-xs"
          />
        </div>
        <div className="flex gap-2 text-xs items-center">
          <span className="text-slate-400 w-14 shrink-0">HSS Lead</span>
          <EditableField
            value={ws.hssLead}
            onChange={(v) => set("hssLead", v)}
            className="text-slate-700 font-medium text-xs"
          />
        </div>
      </div>
      {ws.gatingFactor && ws.gatingFactor !== "None" && (
        <div className="mt-3 flex items-start gap-1.5 text-xs text-amber-700 bg-amber-50 rounded-lg px-2.5 py-2">
          <span className="text-amber-500 shrink-0 mt-px">!</span>
          <EditableField
            value={ws.gatingFactor}
            onChange={(v) => set("gatingFactor", v)}
            multi
            className="text-xs text-amber-700"
          />
        </div>
      )}
      <div className="mt-2.5 border-t border-slate-100 pt-2.5">
        <EditableField
          value={ws.notes}
          onChange={(v) => set("notes", v)}
          placeholder="Add notes..."
          multi
          className="text-xs text-slate-400"
        />
      </div>
    </div>
  );
}
